import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { pacsAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';

const MODALITIES = ['', 'XRAY', 'CT', 'MRI', 'USG', 'MAMMO'];

const SCAN_STATUS_COLOR = {
    Ordered: '#64748b', Scheduled: '#7c3aed', 'In Progress': '#3b82f6',
    'Images Ready': '#0891b2', Completed: '#16a34a', Finalized: '#16a34a'
};
const REPORT_STATUS_COLOR = {
    Pending: '#64748b', Draft: '#d97706', Finalized: '#8b5cf6', Approved: '#16a34a', Rejected: '#ef4444'
};

// STAT > 1h, Urgent > 4h, Routine > 24h
const TAT_OVERDUE_MINS = { STAT: 60, Urgent: 240, Routine: 1440 };

function tatStr(orderTime, priority) {
    const mins = Math.floor((Date.now() - new Date(orderTime)) / 60000);
    const limit = TAT_OVERDUE_MINS[priority] || 1440;
    const overdue = mins > limit;
    let color = '#16a34a';
    if (mins >= 60 && mins < limit) color = '#d97706';
    if (overdue) color = '#dc2626';
    const label = mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
    return { label, color, overdue };
}

const SCHEDULE_ROLES = ['super_admin', 'it_manager', 'hospital_admin', 'reception'];

export default function PACSWorklist() {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const canSchedule = SCHEDULE_ROLES.includes(user?.role);

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modality, setModality] = useState('');
    const [search, setSearch] = useState('');
    const [showCompleted, setShowCompleted] = useState(false);
    const prevStatCount = useRef(-1);
    const [historyPatient, setHistoryPatient] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [scheduleModal, setScheduleModal] = useState(null);
    const [schedForm, setSchedForm] = useState({ scheduled_time: '', room: '' });
    const [actionLoading, setActionLoading] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await pacsAPI.getWorklist({ modality });
            setOrders(res.data);
            const statCount = res.data.filter(o => o.priority === 'STAT' && !['Completed','Finalized'].includes(o.status)).length;
            if (statCount > prevStatCount.current && prevStatCount.current >= 0) {
                toast.error(`🚨 NEW STAT ORDER! ${statCount} STAT order(s) pending`, { autoClose: 8000 });
            }
            prevStatCount.current = statCount;
        } catch { setOrders([]); } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [modality]);
    useEffect(() => {
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [modality]);

    const markDone = async (order) => {
        if (!window.confirm(`Mark exam done for ${order.full_name}? Status will become "Images Ready".`)) return;
        setActionLoading(order.id);
        try {
            await pacsAPI.markImagesReady({ order_id: order.id });
            toast.success('Exam marked done. Images ready for radiologist.');
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update status');
        } finally { setActionLoading(null); }
    };

    const submitSchedule = async () => {
        if (!schedForm.scheduled_time) return toast.error('Please select date and time');
        setActionLoading(scheduleModal.id);
        try {
            await pacsAPI.scheduleOrder({ order_id: scheduleModal.id, ...schedForm });
            toast.success('Order scheduled successfully');
            setScheduleModal(null);
            setSchedForm({ scheduled_time: '', room: '' });
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to schedule');
        } finally { setActionLoading(null); }
    };

    const openHistory = async (order) => {
        setHistoryPatient(order);
        setHistory([]);
        setHistoryLoading(true);
        try {
            const res = await pacsAPI.getPatientHistory(order.patient_id);
            setHistory(res.data);
        } catch { toast.error('Failed to load history'); }
        finally { setHistoryLoading(false); }
    };

    const MODALITY_ICONS = { XRAY: '🩻', CT: '🔍', MRI: '🧲', USG: '🫀', MAMMO: '🔬' };
    const ORTHANC_BASE = process.env.REACT_APP_ORTHANC_URL || 'http://204.168.159.106:8042';

    const filtered = orders.filter(o => {
        const isCompleted = ['Completed', 'Finalized', 'Approved'].includes(o.status) || ['Approved', 'Finalized'].includes(o.report_status);
        if (!showCompleted && isCompleted) return false;
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        return (o.full_name || '').toLowerCase().includes(s) || (o.mrn || '').toLowerCase().includes(s);
    });

    const canMarkDone = (status) => ['Scheduled', 'In Progress'].includes(status);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>🩻 PACS / Radiology Worklist</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                    {MODALITIES.map(m => (
                        <button key={m} onClick={() => setModality(m)}
                            style={{ padding: '7px 16px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, fontWeight: modality === m ? 700 : 400,
                                background: modality === m ? '#0369a1' : 'white', color: modality === m ? 'white' : '#374151' }}>
                            {m ? `${MODALITY_ICONS[m] || ''} ${m}` : 'All'}
                        </button>
                    ))}
                </div>
            </div>

            <input
                type="text" placeholder="Search by patient name or MRN..." value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', padding: '9px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, marginBottom: 10, boxSizing: 'border-box', outline: 'none' }}
            />

            <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
                <button onClick={() => setShowCompleted(false)}
                    style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, background: !showCompleted ? '#0369a1' : 'white', color: !showCompleted ? 'white' : '#374151' }}>
                    Active only
                </button>
                <button onClick={() => setShowCompleted(true)}
                    style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, background: showCompleted ? '#0369a1' : 'white', color: showCompleted ? 'white' : '#374151' }}>
                    All orders
                </button>
                <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>{filtered.length} order(s)</span>
            </div>

            {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                    <p style={{ color: '#64748b' }}>No radiology orders found</p>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Priority', 'Order No.', 'Patient · Ordered By', 'Modality', 'TAT', 'Scan Status', 'Report Status', ''].map(h => (
                                    <th key={h} style={{ padding: '12px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((o) => {
                                const isStat = o.priority === 'STAT';
                                const tat = tatStr(o.order_time, o.priority);
                                const isDone = ['Completed', 'Finalized'].includes(o.status);
                                return (
                                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9', background: isStat ? '#fef2f2' : 'white' }}>
                                    <td style={{ padding: '10px 10px' }}>
                                        {isStat
                                            ? <span style={{ background: '#dc2626', color: 'white', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 900 }}>🚨 STAT</span>
                                            : <span style={{ background: o.priority === 'Urgent' ? '#fff7ed' : '#f0fdf4', color: o.priority === 'Urgent' ? '#ea580c' : '#16a34a', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{o.priority}</span>
                                        }
                                    </td>
                                    <td style={{ padding: '10px 10px', fontSize: 12, fontWeight: 700, color: '#0369a1', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                                        onClick={() => navigate(`/pacs/reporting?order_id=${o.id}`)}>
                                        {o.order_number}
                                    </td>
                                    <td style={{ padding: '10px 10px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{o.full_name}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.mrn} · {o.age_years}y {o.gender} · {new Date(o.order_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>👤 {o.ordered_by_name}</div>
                                    </td>
                                    <td style={{ padding: '10px 10px' }}>
                                        <span style={{ fontWeight: 700, fontSize: 13 }}>{MODALITY_ICONS[o.modality] || ''} {o.modality}</span>
                                        {o.is_contrast && <span style={{ marginLeft: 4, background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>+C</span>}
                                        {o.test_name && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{o.test_name}</div>}
                                    </td>
                                    <td style={{ padding: '10px 10px' }}>
                                        <span style={{ fontWeight: 700, fontSize: 12, color: tat.color }}>{tat.label}</span>
                                        {tat.overdue && <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 700 }}>⚠ OVERDUE</div>}
                                    </td>
                                    <td style={{ padding: '10px 10px' }}>
                                        <span style={{ background: (SCAN_STATUS_COLOR[o.status] || '#64748b') + '22', color: SCAN_STATUS_COLOR[o.status] || '#64748b', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                                            {o.status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 10px' }}>
                                        <span style={{ background: (REPORT_STATUS_COLOR[o.report_status] || '#64748b') + '22', color: REPORT_STATUS_COLOR[o.report_status] || '#64748b', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                                            {['Images Ready','In Progress','Ordered','Scheduled'].includes(o.report_status) ? 'Pending' : (o.report_status || 'Pending')}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 10px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {canSchedule && ['Ordered','Scheduled'].includes(o.status) && (
                                                <button onClick={() => { setScheduleModal(o); setSchedForm({ scheduled_time: o.scheduled_time ? o.scheduled_time.slice(0,16) : '', room: o.room || '' }); }}
                                                    style={{ padding: '4px 8px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
                                                    🗓 Schedule
                                                </button>
                                            )}
                                            {canMarkDone(o.status) && (
                                                <button onClick={() => markDone(o)} disabled={actionLoading === o.id}
                                                    style={{ padding: '4px 8px', background: '#0891b2', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
                                                    ✅ Exam Done
                                                </button>
                                            )}
                                            <button onClick={() => openHistory(o)}
                                                style={{ padding: '4px 8px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, cursor: 'pointer', fontSize: 10, color: '#374151' }}>
                                                📋 History
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Schedule Modal */}
            {scheduleModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setScheduleModal(null)}>
                    <div style={{ background: 'white', borderRadius: 12, width: 420, padding: 28 }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', margin: 0 }}>🗓 Schedule Exam</h3>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>{scheduleModal.full_name} — {scheduleModal.modality} {scheduleModal.body_part}</p>
                            </div>
                            <button onClick={() => setScheduleModal(null)} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', padding: '4px 10px' }}>✕</button>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Date & Time *</label>
                            <input type="datetime-local" value={schedForm.scheduled_time}
                                onChange={e => setSchedForm(f => ({ ...f, scheduled_time: e.target.value }))}
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Room / Suite</label>
                            <input type="text" placeholder="e.g. CT Room 1, MRI Suite A" value={schedForm.room}
                                onChange={e => setSchedForm(f => ({ ...f, room: e.target.value }))}
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={submitSchedule} disabled={actionLoading === scheduleModal.id}
                                style={{ flex: 1, padding: '11px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                                Confirm Schedule
                            </button>
                            <button onClick={() => setScheduleModal(null)}
                                style={{ padding: '11px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Patient History Modal */}
            {historyPatient && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setHistoryPatient(null)}>
                    <div style={{ background: 'white', borderRadius: 12, width: 820, maxHeight: '80vh', overflow: 'auto', padding: 24 }}
                        onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0369a1', margin: 0 }}>📋 Radiology History</h3>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>{historyPatient.full_name} · {historyPatient.mrn}</p>
                            </div>
                            <button onClick={() => setHistoryPatient(null)}
                                style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', padding: '4px 10px', fontSize: 13 }}>✕</button>
                        </div>
                        {historyLoading ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
                        ) : history.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No previous radiology orders found</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        {['Date', 'Order No.', 'Modality', 'Exam', 'Ordered By', 'Status', 'Findings'].map(h => (
                                            <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(h => (
                                        <tr key={h.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '9px 10px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{new Date(h.order_time).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                                            <td style={{ padding: '9px 10px', fontSize: 11, fontWeight: 700, color: '#0369a1', whiteSpace: 'nowrap' }}>{h.order_number}</td>
                                            <td style={{ padding: '9px 10px', fontSize: 12, fontWeight: 600 }}>{MODALITY_ICONS[h.modality] || ''} {h.modality}</td>
                                            <td style={{ padding: '9px 10px', fontSize: 11 }}>{h.test_name || h.body_part || '—'}</td>
                                            <td style={{ padding: '9px 10px', fontSize: 11, color: '#64748b' }}>{h.ordered_by_name || '—'}</td>
                                            <td style={{ padding: '9px 10px' }}>
                                                <span style={{ background: (REPORT_STATUS_COLOR[h.report_status] || '#64748b') + '22', color: REPORT_STATUS_COLOR[h.report_status] || '#64748b', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                                    {h.report_status || 'Pending'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '9px 10px', fontSize: 11, color: '#374151', maxWidth: 220 }}>
                                                {h.findings
                                                    ? <span title={h.findings}>{h.findings.substring(0, 70)}{h.findings.length > 70 ? '...' : ''}</span>
                                                    : <span style={{ color: '#94a3b8' }}>—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
